import { Injectable, inject } from '@angular/core';
import { MetricService } from './metric.service';
import { UserJourneyEvent } from './user-journey.types';
import { GaEcommerceService } from './ga-ecommerce.service';
import {
  buildSubscriptionItem,
  clearGaCheckoutContext,
  extractTransactionIdFromPayLink,
  freeVsBasicGaItems,
  loadGaCheckoutContext,
  normalizeCurrency,
  plansListToGaItems,
  projectTariffsToGaItems,
  roundMoney,
  saveGaCheckoutContext,
  tokenPlanToGaItem,
} from './ga-ecommerce.mapper';
import { GaEcommerceCurrency, GaEcommerceItem } from './ga-ecommerce.types';
import { Plan } from '../../interfaces/tariffs.interface';
import { LegacySubscriptionPlansList } from './ga-ecommerce.mapper';
import { ProjectTariffsData } from '../../shared/services/api/project-tariffs.service';
import {
  SubscribePeriodEnum,
  SubscriptionType,
} from '../../pages/workspace/features/create-workspace/services/workspace-api.service';

@Injectable({
  providedIn: 'root',
})
export class MonetizationAnalyticsService {
  private metric = inject(MetricService);
  private gaEcommerce = inject(GaEcommerceService);

  trackViewPricingList(params: {
    items: GaEcommerceItem[];
    currency?: GaEcommerceCurrency;
    payload?: Record<string, unknown>;
  }): void {
    this.metric.track({
      name: UserJourneyEvent.VIEW_PRICING,
      payload: params.payload,
    });
    this.gaEcommerce.viewItemList(params.items, normalizeCurrency(params.currency));
  }

  trackViewPricingFromPlansList(
    tariffsData: LegacySubscriptionPlansList,
    payload?: Record<string, unknown>,
  ): void {
    this.trackViewPricingList({
      items: plansListToGaItems(tariffsData),
      payload,
    });
  }

  trackViewPricingFromProjectTariffs(
    tariffData: ProjectTariffsData,
    period: 'month' | 'year',
    seats = 1,
    payload?: Record<string, unknown>,
  ): void {
    this.trackViewPricingList({
      items: projectTariffsToGaItems(tariffData, period, seats),
      payload,
    });
  }

  trackViewFreeVsBasicPaywall(payload?: Record<string, unknown>): void {
    this.trackViewPricingList({
      items: freeVsBasicGaItems(),
      payload,
    });
  }

  trackSelectPlan(params: {
    subscriptionType: SubscriptionType | string;
    period: SubscribePeriodEnum | 'month' | 'year';
    seats: number;
    totalValue: number;
    payload?: Record<string, unknown>;
  }): void {
    const item = buildSubscriptionItem({
      subscriptionType: params.subscriptionType,
      period: params.period,
      seats: params.seats,
      totalValue: params.totalValue,
    });

    this.metric.track({
      name: UserJourneyEvent.SELECT_PLAN,
      payload: params.payload,
    });
    this.gaEcommerce.selectItem(item);
  }

  trackSelectPlanFromTariffItem(
    tariffType: SubscriptionType,
    tariffPrice: number,
    seats: number,
    yearly: boolean,
    payload?: Record<string, unknown>,
  ): void {
    const period = yearly ? SubscribePeriodEnum.YEAR : SubscribePeriodEnum.MONTH;
    const dailyRate = tariffPrice * Math.max(1, seats);
    const totalValue = yearly ? dailyRate * 365 : dailyRate * 30;

    this.trackSelectPlan({
      subscriptionType: tariffType,
      period,
      seats,
      totalValue,
      payload,
    });
  }

  trackBeginCheckout(params: {
    payLink: string;
    subscriptionType: SubscriptionType | string;
    period: SubscribePeriodEnum | 'month' | 'year';
    seats: number;
    totalValue: number;
    currency?: GaEcommerceCurrency;
    itemCategory?: string;
    payload?: Record<string, unknown>;
  }): void {
    const currency = normalizeCurrency(params.currency);
    const items = [
      buildSubscriptionItem({
        subscriptionType: params.subscriptionType,
        period: params.period,
        seats: params.seats,
        totalValue: params.totalValue,
        itemCategory: params.itemCategory,
      }),
    ];
    const value = roundMoney(params.totalValue);

    this.metric.track({
      name: UserJourneyEvent.START_PAYMENT,
      payload: params.payload,
    });

    if (params.payLink) {
      saveGaCheckoutContext({
        payLink: params.payLink,
        value,
        currency,
        items,
      });
    }

    this.gaEcommerce.beginCheckout({ value, currency, items });
  }

  trackBeginTokenCheckout(params: {
    payLink: string;
    tokenCount: number;
    totalValue: number;
    payload?: Record<string, unknown>;
  }): void {
    const currency = DEFAULT_GA_CURRENCY;
    const items = [tokenPlanToGaItem(params.tokenCount, params.totalValue)];
    const value = roundMoney(params.totalValue);

    this.metric.track({
      name: UserJourneyEvent.START_PAYMENT,
      payload: params.payload,
    });

    if (params.payLink) {
      saveGaCheckoutContext({
        payLink: params.payLink,
        value,
        currency,
        items,
      });
    }

    this.gaEcommerce.beginCheckout({ value, currency, items });
  }

  trackPurchaseComplete(params: {
    payLink?: string;
    payload?: Record<string, unknown>;
    value?: number;
    items?: GaEcommerceItem[];
    currency?: GaEcommerceCurrency;
    transactionId?: string;
  }): void {
    const stored = params.payLink ? loadGaCheckoutContext(params.payLink) : null;
    const currency = normalizeCurrency(params.currency ?? stored?.currency);
    const items = params.items ?? stored?.items ?? [];
    const value = roundMoney(params.value ?? stored?.value ?? 0);
    const transaction_id = params.payLink
      ? extractTransactionIdFromPayLink(params.payLink)
      : (params.transactionId ?? `order_${Date.now()}`);

    this.metric.track({
      name: UserJourneyEvent.PURCHASE_COMPLETE,
      payload: params.payload,
    });

    if (items.length > 0 && value >= 0) {
      this.gaEcommerce.purchase({
        transaction_id,
        value,
        currency,
        items,
      });
    }

    if (params.payLink) {
      clearGaCheckoutContext();
    }
  }

  trackPurchaseApplied(params: {
    subscriptionType: SubscriptionType | string;
    period: SubscribePeriodEnum | 'month' | 'year';
    seats: number;
    totalValue: number;
    payload?: Record<string, unknown>;
  }): void {
    const items = [
      buildSubscriptionItem({
        subscriptionType: params.subscriptionType,
        period: params.period,
        seats: params.seats,
        totalValue: params.totalValue,
      }),
    ];

    this.trackPurchaseComplete({
      value: params.totalValue,
      items,
      transactionId: `applied_${Date.now()}`,
      payload: params.payload,
    });
  }

  trackPaymentFailed(payload?: Record<string, unknown>): void {
    this.metric.track({
      name: UserJourneyEvent.PAYMENT_FAILED,
      payload,
    });
    clearGaCheckoutContext();
  }

  trackViewTokens(params: { tokenCount: number; totalValue: number; payload?: Record<string, unknown> }): void {
    const item = tokenPlanToGaItem(params.tokenCount, params.totalValue);
    this.gaEcommerce.viewItem(item);
    this.metric.track({
      name: UserJourneyEvent.VIEW_PRICING,
      payload: { ...params.payload, purchaseType: 'tokens' },
    });
  }
}

const DEFAULT_GA_CURRENCY = normalizeCurrency();
